// legal/HaulerTerms.js
// LoadDash Hauler Terms of Service. First-login acknowledgment gate.
// Renders the full document as a scrollable card with a single
// acknowledgment checkbox at the bottom. Shown one time only on
// first login. Once accepted, has_accepted_hauler_terms() returns
// true and this gate is bypassed forever for that user.
//
// ─────────────────────────────────────────────────────────────────
// LEGAL NOTICE. REVIEW REQUIRED BEFORE PRODUCTION SCALE.
// This document captures the platform's commercial intent in
// plain English. It is NOT a substitute for attorney-drafted
// legal language. Before onboarding Hauler #2 or any non-test
// contractor, have counsel review:
//   - Section 5 (Items You Choose to Accept Are At Your Risk).
//     The "recommendation plus consequence" framing depends on
//     this clause being clearly drafted. Counsel should confirm
//     it interlocks correctly with the indemnification clause.
//   - Section 9 (Indemnification and Limitation of Liability).
//     Liability caps must comply with state consumer protection
//     law. Indemnification language varies by state.
//   - Section 14 (Dispute Resolution). Arbitration clauses
//     require specific drafting to be enforceable. Class action
//     waiver enforceability varies.
//   - All survival, severability, and integration clauses.
// Background check authorization was intentionally omitted:
// Boyer Hill base access is the existing vetting mechanism for
// Hill AFB. If the platform expands to non-base-access markets,
// counsel should draft FCRA-compliant disclosure and
// authorization language at that time.
// Off-platform non-solicit was intentionally omitted: Haulers
// are free to perform similar work for any party off the
// platform. LoadDash does not restrict their other business
// activities.
// Bump the agreement_version constant below and rev to v2 once
// counsel has reviewed and rewritten as needed.
// ─────────────────────────────────────────────────────────────────
//
// Original IP. LoadDash Services LLC.

import React, { useState, useRef, useEffect } from "react";
import { supabase } from "../services/supabaseService";

const NAVY = "#0a1628";
const BLUE = "#3D90D9";
const GREEN = "#03A66A";
const RED = "#e53935";
const STEEL = "#607d9f";
const MUTED = "#8fa5be";
const BORDER = "#dce4ef";
const BG = "#f4f6f9";

const AGREEMENT_VERSION = "hauler-terms-v1.1";

const SECTIONS = [
  {
    title: "1. When this agreement starts",
    body: (
      <>When you check the box below, this agreement starts. It applies to every job you do on the platform from that moment forward. If we update these terms, we will let you know, and your next session confirms the new version.</>
    ),
  },
  {
    title: "2. You run your own business",
    body: (
      <>
        You are an independent contractor, not an employee. You decide your hours. You pick the jobs you want. You bring your own truck, your own gear, and your own crew. We do not tell you how to do the work, and we do not manage you.
        <br /><br />
        You also keep your other work. You are free to haul, move, clean out, or run your own jobs for anyone off the platform. Nothing here restricts what you do outside LoadDash.
        <br /><br />
        Because you are a contractor, you handle your own taxes, your own insurance, and your own equipment. We will send you a 1099-NEC at year end if you cross the IRS reporting threshold (see Section 11).
      </>
    ),
  },
  {
    title: "3. We connect, you transport",
    body: (
      <>LoadDash is a technology platform. We connect Boyer Hill residents with Haulers like you. We do not pick up trash, we do not drive trucks, and we do not take ownership of anyone's items. We run the booking, job board, payment, and notification systems so you and the customer can transact directly. Haulers choose their own jobs from the board. LoadDash does not assign jobs to specific Haulers.</>
    ),
  },
  {
    title: "4. Keep your rig roadworthy and legal",
    body: (
      <>
        Your truck and your license are your livelihood. Keep them in good standing while you operate on the platform:
        <ul style={{ margin: "10px 0", paddingLeft: "22px" }}>
          <li style={{ marginBottom: 6 }}>Hold a valid, in-good-standing driver's license from a U.S. state.</li>
          <li style={{ marginBottom: 6 }}>Carry auto liability insurance at or above your state's legal minimum.</li>
          <li style={{ marginBottom: 6 }}>Keep your registration current and your vehicle roadworthy.</li>
          <li style={{ marginBottom: 6 }}>Run a quick pre-trip safety check before each operating day.</li>
        </ul>
        Tell us within 48 hours if anything changes that affects your eligibility. That includes a license issue, an insurance lapse, a vehicle swap, or anything else that affects whether you can take jobs.
      </>
    ),
    critical: true,
  },
  {
    title: "5. Some loads aren't worth the risk",
    body: (
      <>
        We have seen what the wrong load can do to a Hauler, a truck, or a back. Skip these whenever you can:
        <ul style={{ margin: "10px 0", paddingLeft: "22px" }}>
          <li style={{ marginBottom: 6 }}>Any single item over 50 pounds.</li>
          <li style={{ marginBottom: 6 }}>Asbestos, lead paint, biohazardous or medical waste.</li>
          <li style={{ marginBottom: 6 }}>Paint and solvents in liquid form, batteries of any chemistry.</li>
          <li style={{ marginBottom: 6 }}>Fuels, ammunition, firearms, fireworks, anything explosive or flammable.</li>
          <li style={{ marginBottom: 6 }}>Anything banned from regular waste disposal under federal, state, or local law.</li>
          <li style={{ marginBottom: 6 }}>Anything that does not feel safe given your truck, your crew, or the day.</li>
        </ul>
        <strong>You make the call. You also own the outcome.</strong> If you accept one of these loads anyway, LoadDash is not part of that decision and is not responsible for any injury, illness, property damage, regulatory penalty, or other consequence that follows. The indemnification in Section 9 applies in full.
        <br /><br />
        Use the platform's flag system to document any load you decline and the reason. You are never penalized for declining an unsafe load.
      </>
    ),
    critical: true,
  },
  {
    title: "6. Treat customers like the neighbors they are",
    body: (
      <>
        Boyer Hill customers are your neighbors. Treat them that way:
        <ul style={{ margin: "10px 0", paddingLeft: "22px" }}>
          <li style={{ marginBottom: 6 }}>Stay outside their homes unless they specifically invite you in for the pickup.</li>
          <li style={{ marginBottom: 6 }}>Do not record, photograph, or video customers, their homes, or their families without consent.</li>
          <li style={{ marginBottom: 6 }}>Leave firearms, alcohol, and unprescribed controlled substances off customer property.</li>
          <li style={{ marginBottom: 6 }}>Keep it professional. No harassment, no discrimination, no threats of any kind.</li>
        </ul>
        If you see something concerning, an unsafe condition, possible illegal activity, or a customer welfare issue, tell us within 24 hours so we can help.
      </>
    ),
  },
  {
    title: "7. Keep customer info on the platform",
    body: (
      <>
        Customer information you see on the platform stays on the platform. That includes names, addresses, phone numbers, gate codes, access notes, item photos, and pickup details. Use it only to complete the specific pickup you accepted through the platform. Do not store it on your phone, do not share it with anyone, do not post it anywhere, and do not sell it.
        <br /><br />
        This obligation outlives your time on the platform. Even after you stop hauling for LoadDash, the customer information you saw while you were here stays confidential.
        <br /><br />
        Nothing here stops you from working with customers you find through your own marketing or your own contacts off the platform. Just do not carry our customer list out the door.
      </>
    ),
    critical: true,
  },
  {
    title: "8. You handle damage you cause",
    body: (
      <>If you scratch a wall, knock over a planter, or cause any other property damage during a pickup, that is on you and your insurance. We will run a good-faith investigation if a customer reports something, and we will cooperate with your insurer on the claim. If the cost is documented and clearly yours, we may deduct it from what we owe you on future jobs, or ask you to pay it directly.</>
    ),
  },
  {
    title: "9. You cover claims that come from your work",
    body: (
      <>
        If a claim comes out of your work on the platform, you cover it. That includes claims from your driving, your handling of a load, your interaction with a customer, or your violation of these terms or any law. "Cover it" means you defend LoadDash, you pay the costs and attorneys' fees, and you keep LoadDash and our team out of the line of fire. This applies in full to loads you accept after we recommended you decline them under Section 5.
        <br /><br />
        On the other side, our liability to you for anything related to the platform or these terms is capped at what we have paid you in the last 12 months. We are not responsible for indirect, incidental, consequential, or punitive damages. That is the deal both ways.
      </>
    ),
    critical: true,
  },
  {
    title: "10. How this partnership ends",
    body: (
      <>
        Either side can end it. You can stop hauling for LoadDash any time by stopping new jobs and signing out. We can pause or end your access for cause or no cause, with or without notice. We try to keep good Haulers running, and we use this discretion when something real comes up: a violation of these terms, a loss of base access, an insurance or license issue, a pattern of customer complaints, a reliability score that has fallen too far, or a major policy shift.
        <br /><br />
        Some obligations outlive the relationship. After you stop hauling, you still owe LoadDash on Sections 7 (customer data), 8 (damage you caused while you were here), 9 (indemnification and liability cap), and 14 (dispute resolution).
      </>
    ),
  },
  {
    title: "11. You handle your own taxes",
    body: (
      <>You are a contractor, so your taxes are yours. We do not withhold from your pay. If you cross the IRS reporting threshold in a calendar year, we send you a 1099-NEC at year end. Send us a current W-9 when you sign up, and update it within 30 days if anything changes.</>
    ),
  },
  {
    title: "12. How we update these terms",
    body: (
      <>We will update these terms now and then. When we do, we will post the new version in the Hauler app, email you the change, or show a notice on screen. For material changes, we give you at least 7 days' heads-up, unless the law or an urgent situation requires us to move faster. Your next session after the update means you accept it. If you do not want to accept an update, stop using the platform.</>
    ),
  },
  {
    title: "13. The legal basics",
    body: (
      <>
        A few standard items so everything works:
        <ul style={{ margin: "10px 0", paddingLeft: "22px" }}>
          <li style={{ marginBottom: 6 }}>You consent to do business with us electronically. The box you check below is your legally binding electronic signature under the federal E-SIGN Act and Utah's adoption of the Uniform Electronic Transactions Act.</li>
          <li style={{ marginBottom: 6 }}>Utah law governs this agreement, without regard to conflict-of-law rules.</li>
          <li style={{ marginBottom: 6 }}>If a court ever decides part of this agreement is not enforceable, the rest still stands, and the unenforceable part gets narrowed just enough to work.</li>
        </ul>
      </>
    ),
  },
  {
    title: "14. If something goes wrong between us",
    body: (
      <>
        We hope it does not come up, but here is how we handle it if it does:
        <ol style={{ margin: "10px 0", paddingLeft: "22px" }}>
          <li style={{ marginBottom: 6 }}>First, we talk. You and LoadDash try to resolve the issue informally and in good faith for at least 30 days.</li>
          <li style={{ marginBottom: 6 }}>If that does not work, we go to binding arbitration in Salt Lake County, Utah, under a recognized arbitration provider's commercial rules.</li>
          <li style={{ marginBottom: 6 }}>Either side can still ask a court for emergency injunctive relief to protect intellectual property or confidential information.</li>
          <li style={{ marginBottom: 6 }}>To the extent the law allows, both you and LoadDash give up the right to be part of a class, collective, or representative action.</li>
        </ol>
      </>
    ),
    critical: true,
  },
  {
    title: "15. Conflicts of interest: Boyer Hill staff",
    body: (
      <>
        LoadDash connects Boyer Hill residents with independent Haulers. To keep that relationship clean, current employees and contractors of Boyer Hill Military Housing — including maintenance and property-management staff — are not eligible to operate as Haulers on the platform.
        <br /><br />
        This keeps your job for Boyer Hill and the jobs you would run here from ever colliding. If you currently work for Boyer Hill in any capacity, please do not apply. If your Boyer Hill employment ends, you are welcome to apply then.
        <br /><br />
        Signing up while employed by or contracted to Boyer Hill, or failing to disclose that relationship, is grounds for immediate removal from the platform under Section 10.
      </>
    ),
    critical: true,
  },
];

export default function HaulerTerms({ session, onComplete }) {
  const [acked, setAcked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const scrollRef = useRef(null);

  // Resolve display name from session metadata or email
  const displayName =
    session?.user?.user_metadata?.name?.trim() ||
    session?.user?.email ||
    "Hauler";

  // Track scroll position so the Accept button is only enabled
  // after the user has reached the bottom of the document.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    function onScroll() {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
      if (atBottom) setScrolledToBottom(true);
    }

    el.addEventListener("scroll", onScroll);
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  async function handleAccept() {
    if (!acked || !scrolledToBottom) return;
    setSubmitting(true);
    setError("");
    try {
      const { error: rpcErr } = await supabase.rpc(
        "record_hauler_terms_acceptance",
        {
          full_name: displayName,
          agreed_at: new Date().toISOString(),
        }
      );
      if (rpcErr) throw rpcErr;
      onComplete();
    } catch (e) {
      setError(e.message || "Failed to record acceptance. Try again.");
    }
    setSubmitting(false);
  }

  const canAccept = acked && scrolledToBottom && !submitting;

  return (
    <div style={{ minHeight: "100vh", background: BG, fontFamily: "'Sora',system-ui,sans-serif" }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(160deg, #060e1a, ${NAVY})`, padding: "20px 18px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <img src="/Loaddashbutton.png" alt="LD" style={{ width: 28, height: 28, borderRadius: 7, objectFit: "contain" }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: "#42a5f5" }}>LoadDash Haulers</span>
        </div>
        <h2 style={{ fontWeight: 900, fontSize: 20, color: "#fff", margin: 0, letterSpacing: -0.3 }}>Hauler Terms of Service</h2>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,.45)", marginTop: 4 }}>
          Read these before you start. You will only see this screen once.
        </div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,.30)", marginTop: 2 }}>Version {AGREEMENT_VERSION}</div>
      </div>

      {/* Scrollable terms body */}
      <div
        ref={scrollRef}
        style={{
          margin: "14px 18px 0",
          background: "#fff",
          borderRadius: 14,
          border: `1px solid ${BORDER}`,
          padding: "20px 18px",
          maxHeight: "55vh",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{ fontSize: 14, color: NAVY, lineHeight: 1.7, fontWeight: 500, marginBottom: 16 }}>
          These are the terms we work under together. Read every section. Some are marked <span style={{ color: RED, fontWeight: 700 }}>Critical</span> because they touch safety, customer trust, or your relationship with LoadDash directly.
        </div>

        {SECTIONS.map((s, i) => (
          <div key={i} style={{ marginBottom: 20 }}>
            <div style={{
              fontWeight: 800,
              fontSize: 15,
              color: s.critical ? RED : NAVY,
              marginBottom: 6,
            }}>
              {s.title}
              {s.critical && (
                <span style={{
                  display: "inline-block",
                  marginLeft: 8,
                  fontSize: 9,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 6,
                  background: "#fce4e4",
                  color: RED,
                  verticalAlign: "middle",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}>Critical</span>
              )}
            </div>
            <div style={{
              fontSize: 14,
              color: "#4a6080",
              lineHeight: 1.7,
              ...(s.critical
                ? { background: "#fff5f5", border: `1px solid #fca5a5`, borderRadius: 10, padding: "12px 14px" }
                : {}),
            }}>
              {s.body}
            </div>
          </div>
        ))}

        <div style={{
          fontSize: 12,
          color: MUTED,
          textAlign: "center",
          padding: "12px 0 4px",
          borderTop: `1px solid ${BORDER}`,
        }}>
          End of Hauler Terms of Service · Version {AGREEMENT_VERSION}
        </div>
      </div>

      {/* Scroll hint */}
      {!scrolledToBottom && (
        <div style={{
          textAlign: "center",
          fontSize: 11,
          color: STEEL,
          marginTop: 8,
          fontWeight: 600,
          letterSpacing: 0.5,
        }}>
          ↓ Scroll to the bottom to continue
        </div>
      )}

      {/* Acknowledgment + Accept */}
      <div style={{ padding: "16px 18px 40px" }}>
        <div style={{
          background: "#fff",
          borderRadius: 14,
          border: `1px solid ${BORDER}`,
          padding: "18px 18px",
        }}>
          <div style={{ fontSize: 12, color: STEEL, marginBottom: 10 }}>
            Acknowledging as: <strong style={{ color: NAVY }}>{displayName}</strong>
          </div>

          <label
            onClick={() => scrolledToBottom && setAcked((v) => !v)}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              padding: "12px 0",
              cursor: scrolledToBottom ? "pointer" : "not-allowed",
              opacity: scrolledToBottom ? 1 : 0.5,
              marginBottom: 12,
            }}
          >
            <div style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              border: `2px solid ${acked ? BLUE : BORDER}`,
              background: acked ? BLUE : "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              marginTop: 1,
            }}>
              {acked && <span style={{ color: "#fff", fontSize: 14, fontWeight: 700, lineHeight: 1 }}>✓</span>}
            </div>
            <span style={{ fontSize: 13, color: NAVY, lineHeight: 1.55, fontWeight: 500 }}>
              I have read these Hauler Terms of Service and I understand and agree to them. I understand this acknowledgment is my electronic signature and is legally binding.
            </span>
          </label>

          {error && (
            <div style={{
              background: "#fce4e4",
              border: "1px solid #ef9a9a",
              borderRadius: 10,
              padding: "10px 12px",
              fontSize: 12,
              color: RED,
              marginBottom: 10,
            }}>
              {error}
            </div>
          )}

          <button
            onClick={handleAccept}
            disabled={!canAccept}
            style={{
              width: "100%",
              padding: "14px",
              borderRadius: 12,
              border: "none",
              background: canAccept
                ? `linear-gradient(135deg, #1565c0, #1976d2)`
                : "#b0bec5",
              color: "#fff",
              fontWeight: 800,
              fontSize: 15,
              cursor: canAccept ? "pointer" : "not-allowed",
              fontFamily: "inherit",
              minHeight: 44,
              boxShadow: canAccept ? "0 4px 16px rgba(21,101,192,.35)" : "none",
            }}
          >
            {submitting ? "Recording acceptance..." : "Accept and Continue"}
          </button>

          <div style={{ fontSize: 11, color: MUTED, marginTop: 10, textAlign: "center", lineHeight: 1.5 }}>
            You will only see this screen once. After acceptance you will continue to Hauler setup.
          </div>
        </div>
      </div>
    </div>
  );
}
